'use strict';

const {
  RRule,
  rrulestr
} = require('rrule');
const {
  getMonth,
  getYear,
  lastDayOfMonth,
  differenceInCalendarWeeks,
  differenceInWeeks
} = require('date-fns');
const {
  format,
  dateFromDatetime,
  subWeeks,
  addDays,
  subDays,
  parseDate,
  newUtcDate
} = require('../date');
const days = [{
  id: 'Monday',
  name: 'Monday',
  rruleDay: RRule.MO
}, {
  id: 'Tuesday',
  name: 'Tuesday',
  rruleDay: RRule.TU
}, {
  id: 'Wednesday',
  name: 'Wednesday',
  rruleDay: RRule.WE
}, {
  id: 'Thursday',
  name: 'Thursday',
  rruleDay: RRule.TH
}, {
  id: 'Friday',
  name: 'Friday',
  rruleDay: RRule.FR
}, {
  id: 'Saturday',
  name: 'Saturday',
  rruleDay: RRule.SA
}, {
  id: 'Sunday',
  name: 'Sunday',
  rruleDay: RRule.SU
}];
const recurrencePatterns = [{
  id: 'every-week',
  name: 'Every week'
}, {
  id: 'every-two-weeks',
  name: 'Every two weeks'
}, {
  id: 'first-of-month',
  name: 'First of the month',
  nameWithDay: day => `First ${day} of every month`,
  rruleSetPosition: 1
}, {
  id: 'second-of-month',
  name: 'Second of the month',
  nameWithDay: day => `Second ${day} of every month`,
  rruleSetPosition: 2
}, {
  id: 'third-of-month',
  name: 'Third of the month',
  nameWithDay: day => `Third ${day} of every month`,
  rruleSetPosition: 3
}, {
  id: 'fourth-of-month',
  name: 'Fourth of the month',
  nameWithDay: day => `Fourth ${day} of every month`,
  rruleSetPosition: 4
}, {
  id: 'last-of-month',
  name: 'Last of the month',
  nameWithDay: day => `Last ${day} of every month`,
  rruleSetPosition: -1
}];
const FREQUENCY_PERIODS = ['year', 'month', 'week'];
const SET_POSITIONS = ['first', 'second', 'third', 'fourth', 'fifth', 'last'];
const frequencies = {
  monthly: 1,
  weekly: 2
};
const sortNumbersAscending = (a, b) => a - b;
const reindexMonthForSchoolYear = month => month - 9 >= 0 ? month - 9 : month + 3;
const getDayExpression = day => days[day].name;
const getFrequencyExpression = (frequency, interval) => {
  const frequencyPeriod = FREQUENCY_PERIODS[frequency];
  if (interval === 1) {
    return `every ${frequencyPeriod}`;
  }
  return `every ${interval} ${frequencyPeriod}s`;
};
const frequencyIsWeekly = (freq, int) => freq === frequencies.weekly && int === 1;
const frequencyIsFortnightly = (freq, int) => freq === frequencies.weekly && int === 2;
const frequencyIsMonthly = freq => freq === frequencies.monthly;
const getSetPositionExpression = setPositions => setPositions.map(setPosition => setPosition === -1 ? 5 : setPosition - 1).sort(sortNumbersAscending).map(setPosition => SET_POSITIONS[setPosition]).join(', ');
const getDatesExpression = (startDate, endDate) => {
  const dateFormat = 'MMMM do';
  if (startDate && !endDate) {
    return `from ${format(parseDate(startDate), dateFormat)}`;
  }
  if (!startDate && endDate) {
    return `until ${format(parseDate(endDate), dateFormat)}`;
  }
  if (startDate && endDate) {
    return `from ${format(parseDate(startDate), dateFormat)} until ${format(parseDate(endDate), dateFormat)}`;
  }
  return '';
};
const getRecurrenceOptions = recurrenceString => {
  const {
    options: {
      byweekday,
      bysetpos,
      freq,
      interval
    }
  } = RRule.fromString(recurrenceString);
  return {
    day: getDayExpression(byweekday),
    frequency: freq,
    setPosition: bysetpos && bysetpos[0],
    interval
  };
};
const getRecurrenceDates = (recurrenceString, startDate, endDate) => {
  const inclusiveStartDate = subDays(startDate, 1);
  const inclusiveEndDate = addDays(endDate, 1);
  const recurrenceOptions = RRule.parseString(recurrenceString);
  recurrenceOptions.dtstart = inclusiveStartDate;
  recurrenceOptions.until = inclusiveEndDate;
  const inclusiveRecurrence = new RRule(recurrenceOptions);
  return inclusiveRecurrence.between(inclusiveStartDate, inclusiveEndDate).map(dateTime => dateFromDatetime(dateTime));
};
const getPotentialRecurrenceDates = (recurrenceString, startDate, endDate) => {
  const {
    frequency,
    interval
  } = getRecurrenceOptions(recurrenceString);
  if (frequencyIsFortnightly(frequency, interval)) {
    const recurrenceOptions = RRule.parseString(recurrenceString);
    recurrenceOptions.interval = 1;
    const equivalentWeeklyRecurrence = new RRule(recurrenceOptions);
    return getRecurrenceDates(equivalentWeeklyRecurrence.toString(), startDate, endDate);
  }
  return getRecurrenceDates(recurrenceString, startDate, endDate);
};
const getDayCode = dayId => {
  const matchedDay = days.find(_ref => {
    let {
      id
    } = _ref;
    return id === dayId;
  });
  return matchedDay && matchedDay.rruleDay;
};
const getSetPositionFromPattern = patternId => {
  const matchedPattern = recurrencePatterns.find(_ref2 => {
    let {
      id
    } = _ref2;
    return id === patternId;
  });
  return matchedPattern && matchedPattern.rruleSetPosition;
};
const getPatternFromSetPosition = setPos => {
  const matchedPattern = recurrencePatterns.find(_ref3 => {
    let {
      rruleSetPosition
    } = _ref3;
    return rruleSetPosition === setPos;
  });
  return matchedPattern;
};
const encodeRecurrence = (day, pattern) => {
  const isWeeklyFrequency = pattern === 'every-week' || pattern === 'every-two-weeks';
  const freq = isWeeklyFrequency ? RRule.WEEKLY : RRule.MONTHLY;
  const byweekday = getDayCode(day);
  const interval = pattern === 'every-two-weeks' && 2;
  const bysetpos = !isWeeklyFrequency && getSetPositionFromPattern(pattern);
  const rule = new RRule({
    freq,
    byweekday,
    ...(interval && {
      interval
    }),
    ...(bysetpos && {
      bysetpos
    })
  });
  return rule.toString();
};
const decodeRecurrence = recurrence => {
  const {
    options: {
      byweekday,
      interval,
      freq,
      bysetpos
    }
  } = rrulestr(recurrence);
  const recurrenceDay = days[byweekday[0]];
  let recurrencePattern;
  if (freq === 2) {
    recurrencePattern = interval === 2 ? recurrencePatterns[1] : recurrencePatterns[0];
  }
  if (freq === 1) {
    recurrencePattern = getPatternFromSetPosition(bysetpos[0]);
  }
  return {
    recurrenceDay,
    recurrencePattern
  };
};
const setPositionIsEndOfMonth = setPosition => [-1, 4].includes(setPosition);
const setPositionsAreCompatible = (setPositionA, setPositionB) => {
  if (setPositionA === setPositionB) {
    return false;
  }
  if (setPositionIsEndOfMonth(setPositionA) && setPositionIsEndOfMonth(setPositionB)) {
    return false;
  }
  return true;
};
const datesAreOnAlternateWeeks = (dateA, dateB) => differenceInCalendarWeeks(parseDate(dateA), parseDate(dateB)) % 2 !== 0;
const filterOutCompatibleOverlappingBookings = (recurrenceString, startDate, overlappingRecurringSpaceBookings) => {
  const {
    frequency,
    interval,
    setPosition
  } = getRecurrenceOptions(recurrenceString);
  if (frequencyIsFortnightly(frequency, interval)) {
    return overlappingRecurringSpaceBookings.filter(_ref4 => {
      let {
        recurringBooking
      } = _ref4;
      const options = getRecurrenceOptions(recurringBooking.recurrenceString);
      return !(frequencyIsFortnightly(options.frequency, options.interval) && datesAreOnAlternateWeeks(startDate, recurringBooking.startDate));
    });
  }
  if (frequencyIsMonthly(frequency)) {
    return overlappingRecurringSpaceBookings.filter(_ref5 => {
      let {
        recurringBooking
      } = _ref5;
      const options = getRecurrenceOptions(recurringBooking.recurrenceString);
      return !(frequencyIsMonthly(options.frequency) && setPositionsAreCompatible(setPosition, options.setPosition));
    });
  }
  return overlappingRecurringSpaceBookings;
};
const getWeekdayNextYear = (date, recurrenceString) => {
  const weekIndexInMonth = differenceInWeeks(date, newUtcDate(getYear(date), getMonth(date) + 1, 1));
  const nextYear = getYear(date) + 1;
  const monthNextYear = getMonth(date);
  const firstOfMonthNextYear = newUtcDate(nextYear, monthNextYear + 1, 1);
  const recurrenceDatesForMonth = getRecurrenceDates(recurrenceString, firstOfMonthNextYear, lastDayOfMonth(firstOfMonthNextYear));
  const dateNextYear = recurrenceDatesForMonth[Math.min(weekIndexInMonth, recurrenceDatesForMonth.length - 1)];
  return dateNextYear;
};
const getRecurrenceStartAndEndDatesForNextYear = _ref6 => {
  let {
    recurrenceString,
    startDate,
    endDate
  } = _ref6;
  const {
    frequency,
    interval
  } = getRecurrenceOptions(recurrenceString);
  const oldStartDate = parseDate(startDate);
  const oldEndDate = parseDate(endDate);
  let newStartDate;
  let newEndDate;
  if (frequencyIsWeekly(frequency, interval)) {
    newStartDate = getWeekdayNextYear(oldStartDate, recurrenceString);
    newEndDate = getWeekdayNextYear(oldEndDate, recurrenceString);
  }
  if (frequencyIsFortnightly(frequency, interval)) {
    newStartDate = getWeekdayNextYear(oldStartDate, recurrenceString);
    newEndDate = getWeekdayNextYear(oldEndDate, recurrenceString);
    if (datesAreOnAlternateWeeks(newEndDate, newStartDate)) {
      newEndDate = subWeeks(newEndDate, 1);
    }
  }
  if (frequencyIsMonthly(frequency)) {
    const firstOfStartMonth = newUtcDate(getYear(oldStartDate) + 1, getMonth(oldStartDate) + 1, 1);
    const lastOfEndMonth = lastDayOfMonth(newUtcDate(getYear(oldEndDate) + 1, getMonth(oldEndDate) + 1, 1));
    const recurrenceDates = getRecurrenceDates(recurrenceString, firstOfStartMonth, lastOfEndMonth);

    // eslint-disable-next-line prefer-destructuring
    newStartDate = recurrenceDates[0];
    newEndDate = recurrenceDates[recurrenceDates.length - 1];
  }
  return {
    startDate: dateFromDatetime(newStartDate),
    endDate: dateFromDatetime(newEndDate)
  };
};
const getRecurrenceExpression = recurrence => {
  const {
    options: {
      freq
    }
  } = rrulestr(recurrence);
  const {
    recurrenceDay,
    recurrencePattern
  } = decodeRecurrence(recurrence);
  if (freq === 1) {
    return recurrencePattern.nameWithDay(recurrenceDay.name);
  }
  return `${recurrenceDay.name}, ${recurrencePattern.name.toLowerCase()}`;
};
module.exports = {
  days,
  recurrencePatterns,
  encodeRecurrence,
  decodeRecurrence,
  reindexMonthForSchoolYear,
  getFrequencyExpression,
  getDayExpression,
  getRecurrenceDates,
  getPotentialRecurrenceDates,
  getDatesExpression,
  getRecurrenceExpression,
  getRecurrenceOptions,
  getSetPositionExpression,
  datesAreOnAlternateWeeks,
  filterOutCompatibleOverlappingBookings,
  frequencyIsMonthly,
  frequencyIsFortnightly,
  getPatternFromSetPosition,
  getRecurrenceStartAndEndDatesForNextYear
};